import React, { useEffect } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import TextInput from "@components/UI/Inputs/TextInput/TextInput";
import { useGetCompanyProfileQuery, useUpdateCompanyProfileMutation } from "@entities/profile";
import { CustomLoader } from "@shared/ui/CustomLoader";
import { VerifiedIcon } from "@shared/ui/Icon/ui/Profile/VerifiedIcon";
import { WaitingIcon } from "@shared/ui/Icon/ui/Profile/WaitingIcon";
import classes from "./CompanyForm.module.scss";

export const CompanyForm = () => {
    const {
        handleSubmit,
        control,
        reset,
        formState: { dirtyFields, errors }
    } = useForm({
        mode: "all"
    });

    const { data: companyData, isLoading } = useGetCompanyProfileQuery();
    const [updateCompanyProfile] = useUpdateCompanyProfileMutation();

    const displayError = (name: string) => {
        const errorMessage = errors?.[name]?.message?.toString();
        return errorMessage ? (
            <span className={classes["company__input--error"]}>{errorMessage}</span>
        ) : null;
    };

    const onSubmit = async (formData: FieldValues) => {
        const updatedFields = {
            ...Object.fromEntries(Object.keys(dirtyFields).map((key) => [key, formData[key]]))
        };

        if (Object.keys(updatedFields).length === 0) {
            return;
        }

        try {
            await updateCompanyProfile(updatedFields).unwrap();
            toast.success("Информация о компании успешно изменена.");
        } catch {
            toast.error("Ошибка при обновлении информации о компании.");
        }
    };

    useEffect(() => {
        if (companyData) {
            reset(companyData);
        }
    }, [companyData, reset]);

    return (
        <form className={classes.company} onSubmit={handleSubmit(onSubmit)}>
            <p className={classes.company__title}>Информация о компании</p>
            {isLoading ? (
                <CustomLoader />
            ) : (
                <>
                    <div className={classes.company__wrapper}>
                        <div className={classes.company__input}>
                            <label className={classes.company__label}>Название компании</label>
                            <Controller
                                control={control}
                                name="name"
                                rules={{
                                    required: "Название компании не может быть пустым"
                                }}
                                render={({ field }) => (
                                    <TextInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        maxLength={255}
                                        placeholder="Введите название компании"
                                    />
                                )}
                            />
                            {displayError("name")}
                            <div className={classes.company__status}>
                                {companyData?.isVerified ? (
                                    <>
                                        <VerifiedIcon />
                                        Проверенный аккаунт
                                    </>
                                ) : (
                                    <>
                                        <WaitingIcon />
                                        Аккаунт на модерации
                                    </>
                                )}
                            </div>
                        </div>
                        <div />
                    </div>
                    <p className={classes.company__title}>Юридическая информация</p>
                    <div className={classes.company__wrapper}>
                        <div className={classes.company__input}>
                            <label className={classes.company__label}>ИНН</label>
                            <Controller
                                control={control}
                                name="inn"
                                rules={{
                                    required: "ИНН не может быть пустым",
                                    pattern: {
                                        value: /^(\d{10}|\d{12})$/,
                                        message: "ИНН должно содержать 10 или 12 цифр"
                                    }
                                }}
                                render={({ field }) => (
                                    <TextInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Введите ИНН"
                                    />
                                )}
                            />
                            {displayError("inn")}
                        </div>
                        <div className={classes.company__input}>
                            <label className={classes.company__label}>КПП</label>
                            <Controller
                                control={control}
                                name="kpp"
                                rules={{
                                    required: "КПП не может быть пустым",
                                    pattern: {
                                        value: /^\d{9}$/,
                                        message: "КПП должен содержать ровно 9 цифр"
                                    }
                                }}
                                render={({ field }) => (
                                    <TextInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Введите КПП"
                                    />
                                )}
                            />
                            {displayError("kpp")}
                        </div>
                        <div className={classes.company__input}>
                            <label className={classes.company__label}>Юридический адрес</label>
                            <Controller
                                control={control}
                                name="legalAddress"
                                render={({ field }) => (
                                    <TextInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Введите юридический адрес"
                                    />
                                )}
                            />
                        </div>
                        <div className={classes.company__input}>
                            <label className={classes.company__label}>ОГРН</label>
                            <Controller
                                control={control}
                                name="ogrn"
                                rules={{
                                    required: "ОГРН не может быть пустым",
                                    pattern: {
                                        value: /^\d+$/,
                                        message: "ОГРН должно содержать только цифры"
                                    }
                                }}
                                render={({ field }) => (
                                    <TextInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Введите ОГРН"
                                    />
                                )}
                            />
                            {displayError("ogrn")}
                        </div>
                        <div className={classes.company__input}>
                            <label className={classes.company__label}>ОКПО</label>
                            <Controller
                                control={control}
                                name="okpo"
                                rules={{
                                    pattern: {
                                        value: /^(\d{8}|\d{10}|\d{14})$/,
                                        message: "ОКПО должно содержать 8, 10 или 14 цифр"
                                    }
                                }}
                                render={({ field }) => (
                                    <TextInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Введите ОКПО"
                                    />
                                )}
                            />
                            {displayError("okpo")}
                        </div>
                    </div>

                    <div className={classes.company__actions}>
                        <button type="submit" className={classes.company__save}>
                            Сохранить
                        </button>
                        <button
                            type="button"
                            className={classes.company__cancel}
                            onClick={() => reset()}
                        >
                            Отмена
                        </button>
                    </div>
                </>
            )}
        </form>
    );
};